import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowLeft, FileText, ChevronUp, ChevronDown, Upload, FileCheck } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/shared";

const FIELD_TYPES = ["long", "short", "sentence", "cols"];
const blank = () => ({ title: "New Worksheet", category: "General", part: "", summary: "", when_to_use: "", related_chapter: "", tags: [], worksheet: { title: "New Worksheet", intro: "", fields: [] } });

export default function TemplateAdmin() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null); // {slug?, ...fields}

  const load = () => api.get("/admin/templates").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { if (!user || user.is_admin) load(); }, [user]);

  if (user && !user.is_admin) return <div className="text-nm-neutral">Admin access required.</div>;
  if (!list) return <Loading label="Loading templates…" />;

  const ta = "w-full rounded-xl border border-nm-primary/15 bg-white px-3.5 py-2.5 text-sm text-nm-ink focus:outline-none focus:ring-2 focus:ring-nm-primary/40";

  const save = async () => {
    if (!editing.part) { toast.error("Please choose a toolkit before saving."); return; }
    const payload = { ...editing, worksheet: { ...editing.worksheet, title: editing.worksheet?.title || editing.title } };
    try {
      if (editing.slug) await api.put(`/admin/templates/${editing.slug}`, payload);
      else await api.post("/admin/templates", payload);
      toast.success("Template saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (t) => {
    if (!window.confirm(t.builtin ? "Reset this built-in template to its original?" : "Delete this template permanently?")) return;
    await api.delete(`/admin/templates/${t.slug}`); toast.success("Done"); load();
  };

  const uploadPdf = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/admin/templates/${editing.slug}/upload-pdf`, fd);
      setEditing({ ...editing, pdf_upload_name: r.data.pdf_upload_name });
      toast.success("PDF uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const removePdf = async () => {
    await api.delete(`/admin/templates/${editing.slug}/pdf`);
    setEditing({ ...editing, pdf_upload_name: null });
    toast.success("PDF removed");
  };

  // ---- Edit form ----
  if (editing) {
    const ws = editing.worksheet || { fields: [] };
    const setWs = (patch) => setEditing({ ...editing, worksheet: { ...ws, ...patch } });
    const setField = (i, patch) => setWs({ fields: ws.fields.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
    const addField = () => setWs({ fields: [...(ws.fields || []), { type: "long", q: "", hint: "", lines: 3 }] });
    const delField = (i) => setWs({ fields: ws.fields.filter((_, idx) => idx !== i) });
    const moveField = (i, d) => { const j = i + d; if (j < 0 || j >= ws.fields.length) return; const a = [...ws.fields]; [a[i], a[j]] = [a[j], a[i]]; setWs({ fields: a }); };
    return (
      <div className="max-w-3xl pb-24" data-testid="template-edit">
        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-sm text-nm-neutral hover:text-nm-primary" data-testid="tpl-back"><ArrowLeft size={15} /> Back to templates</button>
        <h1 className="font-serif text-3xl text-nm-ink mt-4">{editing.slug ? "Edit template" : "New template"}</h1>
        <div className="mt-6 space-y-4 rounded-2xl border border-nm-primary/12 bg-nm-surface p-6">
          <div><label className="text-xs font-semibold uppercase tracking-wide text-nm-neutral">Title</label><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={ta} data-testid="tpl-title" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs font-semibold uppercase tracking-wide text-nm-neutral">Category</label><input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={ta} /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wide text-nm-neutral">Toolkit <span className="text-nm-coral">*</span></label>
              <select value={editing.part} onChange={(e) => setEditing({ ...editing, part: e.target.value })} data-testid="tpl-toolkit" className={`${ta} ${!editing.part ? "border-nm-coral/60 text-nm-neutral" : ""}`}>
                <option value="" disabled>Choose a toolkit…</option>
                <option value="part_1">The Role (Part 1)</option>
                <option value="part_2">The People (Part 2)</option>
                <option value="part_3">The Moments (Part 3)</option>
              </select>
              {!editing.part && <p className="mt-1 text-xs text-nm-coral">Pick which toolkit this template belongs to — it decides where it appears and who can unlock it.</p>}
            </div>
          </div>
          <div><label className="text-xs font-semibold uppercase tracking-wide text-nm-neutral">Summary</label><textarea value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} rows={2} className={ta} /></div>
          <div><label className="text-xs font-semibold uppercase tracking-wide text-nm-neutral">When to use</label><textarea value={editing.when_to_use || ""} onChange={(e) => setEditing({ ...editing, when_to_use: e.target.value })} rows={2} className={ta} data-testid="tpl-when-to-use" /></div>
          <div><label className="text-xs font-semibold uppercase tracking-wide text-nm-neutral">Worksheet intro (top of PDF)</label><textarea value={ws.intro || ""} onChange={(e) => setWs({ intro: e.target.value })} rows={2} className={ta} /></div>
        </div>

        <div className="mt-6">
          <h2 className="font-serif text-xl text-nm-ink">Ready-made PDF</h2>
          {editing.slug ? (
            editing.pdf_upload_name ? (
              <div className="mt-3 flex items-center gap-3 rounded-xl border border-nm-primary/12 bg-nm-surface p-4" data-testid="tpl-pdf-attached">
                <FileCheck size={20} className="text-nm-green shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-nm-ink truncate">{editing.pdf_upload_name}</div>
                  <div className="text-xs text-nm-neutral">This uploaded PDF is served on download instead of the generated worksheet below.</div>
                </div>
                <button onClick={removePdf} className="rounded-full border border-red-200 px-3.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50" data-testid="tpl-pdf-remove">Remove</button>
              </div>
            ) : (
              <div className="mt-3">
                <label className="inline-flex items-center gap-2 rounded-full border border-nm-primary/20 px-4 py-2 text-sm font-medium text-nm-primary hover:bg-nm-primary/5 cursor-pointer" data-testid="tpl-pdf-upload-label">
                  <Upload size={15} /> Upload a PDF
                  <input type="file" accept="application/pdf,.pdf" onChange={uploadPdf} className="hidden" data-testid="tpl-pdf-input" />
                </label>
                <p className="mt-2 text-xs text-nm-neutral">Optional. Upload your own finished PDF and it will be served on download — no need to build fillable fields below.</p>
              </div>
            )
          ) : (
            <p className="mt-2 text-sm text-nm-neutral">Save the template first, then you can upload a ready-made PDF here.</p>
          )}
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between"><h2 className="font-serif text-xl text-nm-ink">Fillable fields</h2><button onClick={addField} className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/20 px-3.5 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5" data-testid="tpl-add-field"><Plus size={13} /> Add field</button></div>
          <div className="mt-4 space-y-3">
            {(ws.fields || []).map((f, i) => (
              <div key={i} className="rounded-xl border border-nm-primary/12 bg-nm-surface p-4" data-testid={`tpl-field-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <select value={f.type} onChange={(e) => setField(i, { type: e.target.value })} className="rounded-lg border border-nm-primary/15 px-2 py-1.5 text-xs">
                    {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input type="number" min="1" max="12" value={f.lines || 3} onChange={(e) => setField(i, { lines: parseInt(e.target.value) || 3 })} className="w-16 rounded-lg border border-nm-primary/15 px-2 py-1.5 text-xs" title="lines" />
                  <div className="ml-auto flex gap-1">
                    <button onClick={() => moveField(i, -1)} className="p-1.5 text-nm-neutral hover:text-nm-primary"><ChevronUp size={15} /></button>
                    <button onClick={() => moveField(i, 1)} className="p-1.5 text-nm-neutral hover:text-nm-primary"><ChevronDown size={15} /></button>
                    <button onClick={() => delField(i)} className="p-1.5 text-red-500 hover:text-red-600" data-testid={`tpl-del-field-${i}`}><Trash2 size={14} /></button>
                  </div>
                </div>
                <input value={f.q || ""} onChange={(e) => setField(i, { q: e.target.value })} placeholder="Question / prompt" className={ta} data-testid={`tpl-field-q-${i}`} />
                <input value={f.hint || ""} onChange={(e) => setField(i, { hint: e.target.value })} placeholder="Hint (optional)" className={`${ta} mt-2`} />
              </div>
            ))}
            {(ws.fields || []).length === 0 && <p className="text-sm text-nm-neutral">No fields yet — add one to build the fillable PDF.</p>}
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button onClick={save} className="inline-flex items-center gap-2 rounded-full bg-nm-coral px-6 py-3 text-sm font-medium text-white hover:brightness-105" data-testid="tpl-save"><Save size={15} /> Save template</button>
          <button onClick={() => setEditing(null)} className="rounded-full border border-nm-primary/15 px-6 py-3 text-sm text-nm-neutral">Cancel</button>
        </div>
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="max-w-4xl pb-24" data-testid="template-admin">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-nm-coral">Admin</div>
          <h1 className="font-serif text-3xl text-nm-ink mt-1">Template Library</h1>
        </div>
        <button onClick={() => setEditing(blank())} className="inline-flex items-center gap-2 rounded-full bg-nm-primary px-5 py-2.5 text-sm font-medium text-white hover:brightness-105" data-testid="tpl-new"><Plus size={15} /> New template</button>
      </div>
      <div className="mt-6 space-y-2.5">
        {list.map((t) => (
          <div key={t.slug} className="flex items-center gap-4 rounded-2xl border border-nm-primary/10 bg-nm-surface px-5 py-4" data-testid={`tpl-row-${t.slug}`}>
            <FileText size={18} className="text-nm-primary shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-nm-ink truncate">{t.title}</div>
              <div className="text-xs text-nm-neutral">{t.category}{t.custom ? " · custom" : " · built-in"}{t.pdf_upload_name ? " · PDF uploaded" : t.worksheet ? ` · ${(t.worksheet.fields || []).length} fields` : " · no worksheet"}</div>
            </div>
            <button onClick={() => setEditing({ slug: t.slug, title: t.title, category: t.category, part: t.part || "part_1", summary: t.summary, when_to_use: t.when_to_use, related_chapter: t.related_chapter, tags: t.tags || [], pdf_upload_name: t.pdf_upload_name, worksheet: t.worksheet || { title: t.title, intro: "", fields: [] } })} className="rounded-full border border-nm-primary/20 px-4 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5" data-testid={`tpl-edit-${t.slug}`}>Edit</button>
            <button onClick={() => del(t)} className="rounded-full border border-red-200 px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50" data-testid={`tpl-delete-${t.slug}`}>{t.builtin && !t.custom ? "Reset" : "Delete"}</button>
          </div>
        ))}
      </div>
      <div className="mt-6"><Link to="/app/templates" className="text-sm text-nm-neutral hover:text-nm-primary">← View the public Template Library</Link></div>
    </div>
  );
}
